import { useCallback, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import IntroReveal from "@/components/IntroReveal";
import HeroSection from "@/components/HeroSection";
import PdfScrollShowcase from "@/components/PdfScrollShowcase";
import BookVideoPreview from "@/components/BookVideoPreview";
import FeatureSection from "@/components/FeatureSection";
import DigitalPlannerShowcase from "@/components/DigitalPlannerShowcase";
import HowItWorks from "@/components/HowItWorks";
import OrderNowLink from "@/components/OrderNowLink";
import { LAUNCH_COUPON_CODE, LAUNCH_COUPON_DISCOUNT, LAUNCH_COUPON_LIMIT } from "@/lib/order";

export default function Landing() {
  const [introDone, setIntroDone] = useState(false);
  const finishIntro = useCallback(() => setIntroDone(true), []);

  return (
    <div className="relative bg-[#FBF7EF]">
      <IntroReveal onFinish={finishIntro} />
      <motion.div
        initial={{ opacity: 0, y: 18, filter: "blur(12px)" }}
        animate={introDone ? { opacity: 1, y: 0, filter: "blur(0px)" } : { opacity: 0, y: 18, filter: "blur(12px)" }}
        transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
      >
        <Navbar />
        <HeroSection />
        <BookVideoPreview />
        <PdfScrollShowcase />
        <FeatureSection />
        <DigitalPlannerShowcase />
        <HowItWorks />
        <OrderCTA />
        <FloatingOrderButton />
        <Footer />
      </motion.div>
    </div>
  );
}

function OrderCTA() {
  return (
    <section id="order" className="bg-[#073C2F] px-4 py-16 text-[#FBF7EF] sm:px-5 md:px-10 md:py-20">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.85fr_1.15fr]">
        <div>
          <p className="font-body text-sm font-bold uppercase tracking-[0.28em] text-[#D5AD76]">Launch Offer</p>
          <h2 className="mt-3 font-heading text-3xl uppercase leading-[1.04] sm:text-4xl md:text-6xl">
            Order Note Console Now
          </h2>
          <p className="mt-5 max-w-lg font-body text-base leading-7 text-[#FBF7EF]/76 md:text-lg md:leading-8">
            Get the MBA planner built for classes, committees, competitions, internships, placements, and the beautiful chaos around them.
          </p>
          <div className="mt-6 max-w-xl rounded-xl border border-[#D5AD76] bg-white px-4 py-4 font-body text-[#073C2F] shadow-[6px_6px_0_rgba(0,0,0,0.32)] sm:px-5">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#8B5B2B]">Coupon Code</p>
            <p className="mt-2 font-heading text-4xl uppercase leading-none">{LAUNCH_COUPON_CODE}</p>
            <p className="mt-2 text-sm font-bold">
              Use it to get {LAUNCH_COUPON_DISCOUNT} off. Offer valid for the {LAUNCH_COUPON_LIMIT}.
            </p>
          </div>
          <p className="mt-5 inline-flex rounded-lg border border-[#D5AD76] bg-[#FBF7EF] px-4 py-2 font-body text-sm font-black uppercase tracking-[0.08em] text-[#073C2F]">
            Free Shipping for Now
          </p>
          <OrderNowLink source="order-section" className="retro-btn mt-6 inline-flex items-center justify-center bg-[#FBF7EF] px-5 py-3 font-body text-sm font-black uppercase text-[#073C2F]" />
        </div>
        <div className="rounded-lg border border-[#D5AD76] bg-[#FBF7EF] p-4 text-retro-ink shadow-[0_24px_80px_rgba(0,0,0,0.22)] sm:p-5 md:p-7">
          <div className="flex flex-col gap-2 border-b border-[#D5AD76] pb-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-body text-sm font-black uppercase tracking-[0.18em] text-[#8B5B2B]">Buying Options</p>
              <h3 className="mt-2 font-heading text-2xl uppercase leading-tight text-[#073C2F]">Choose a Plan at Checkout</h3>
            </div>
            <p className="font-body text-sm font-bold text-[#073C2F]/70">Use coupon {LAUNCH_COUPON_CODE} for {LAUNCH_COUPON_DISCOUNT} off. Free delivery for now.</p>
          </div>
          <div className="mt-5 grid gap-3">
            <BuyingOption number="1" title="Black and White" price="Rs. 399" note="Essential planner layout in crisp monochrome." />
            <BuyingOption number="2" title="Coloured" price="Rs. 499" note="Full-colour planner pages with digital companion access." />
            <BuyingOption number="3" title="Coloured with Personalized Writer Message and Gift" price="Rs. 599" note="A premium copy with a personal message and gifting touch." />
          </div>
          <OrderNowLink source="buying-options" className="retro-btn mt-6 inline-flex w-full items-center justify-center bg-retro-purple px-5 py-3 font-body text-sm font-black uppercase text-white" />
        </div>
      </div>
    </section>
  );
}

function BuyingOption({ number, title, price, note }) {
  return (
    <div className="rounded-lg border-[2.5px] border-retro-ink bg-white px-4 py-4 shadow-[4px_4px_0_rgba(7,60,47,0.16)]">
      <div className="grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-start">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-full border-2 border-retro-ink bg-[#073C2F] font-body text-sm font-black text-white">
            {number}
        </div>
        <div className="min-w-0">
          <h4 className="font-heading text-lg uppercase leading-tight text-[#073C2F] sm:text-xl">{title}</h4>
          <p className="mt-1 font-body text-sm font-semibold leading-6 text-[#073C2F]/70">{note}</p>
        </div>
          <div className="text-left sm:text-right">
            <p className="font-heading text-2xl uppercase leading-none text-[#073C2F] sm:text-3xl">{price}</p>
            <p className="mt-1 font-body text-xs font-black uppercase tracking-[0.04em] text-[#073C2F]/62">(+ GST, free delivery)</p>
          </div>
      </div>
    </div>
  );
}

function FloatingOrderButton() {
  return (
    <OrderNowLink
      source="floating"
      className="retro-btn fixed bottom-4 right-4 z-50 inline-flex max-w-[calc(100vw-2rem)] items-center justify-center bg-retro-yellow px-4 py-3 font-body text-sm font-black uppercase text-retro-ink shadow-[6px_6px_0_#111111] sm:bottom-6 sm:right-6 sm:px-5"
      data-testid="floating-order-now"
    >
      Order Now
    </OrderNowLink>
  );
}
